import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PDF from './PDF/PDF';
import SkeletonPDF from '../../LoadingPages/SkeletonPDF';
import css from './style.module.css';
import { motion } from "framer-motion";
import Attestation from '../../Services/Attestation';
import BackSection from '../../Services/BackSection';
import { fetchPolicyDocument, acknowledgePolicyDocument } from '../PoliciesApi';
import { PORTAL_ORIGINS } from '../../../lib/hosts';

export default function PoliciesDetails() {
    const { id } = useParams();
    const location = useLocation();
    const { Name } = location.state.policy;
    const navigation = useNavigate();
    const queryClient = useQueryClient();

    const { protocol, hostname, port } = window.location;
    const fullUrl = `${protocol}//${hostname}${port ? `:${port}` : ""}/`;

    const [isAcknowledged, setIsAcknowledged] = useState(false);
    const [appear, setAppear] = useState(false);
    const [isAllowed, setIsAllowed] = useState(false);

    const allowedUrl = PORTAL_ORIGINS.map((o) => `${o}/`);

    useEffect(() => {
        if (allowedUrl.includes(fullUrl)) {
            setIsAllowed(true);
        }
    }, [fullUrl]);

    // Fetch policy details with useQuery
    const { data: details, isLoading, error } = useQuery({
        queryKey: ['policyDetails', id],
        queryFn: () => fetchPolicyDocument(id),
        enabled: !!id, // Only fetch if id exists
        staleTime: 5 * 60 * 1000, // 5 minutes
    });

    // Acknowledge mutation
    const acknowledgeMutation = useMutation({
        mutationFn: () => acknowledgePolicyDocument(id),
        onSuccess: () => {
            // Update the cache to reflect the acknowledgement
            queryClient.setQueryData(['policyDetails', id], (oldData) => ({
                ...oldData,
                userAcknowledgement: true,
            }));
            setAppear(true);
        },
        onError: (error) => {
            console.error("Error acknowledging policy:", error);
        },
    });

    const handleAcknowledgeChange = (e) => {
        setIsAcknowledged(e.target.checked);
    };

    const handleSave = () => {
        acknowledgeMutation.mutate();
    };

    // Handle 401 errors (you might want to do this in axios interceptor instead)
    useEffect(() => {
        if (error?.message.includes('401')) {
            localStorage.removeItem("Firebase Auth");
            localStorage.removeItem("okta-token-storage");
            localStorage.removeItem("okta-cache-storage");
            navigation('/');
        }
    }, [error, navigation]);

    const targetPath = '/policies';
    const label = "Back to Policies Page";
    const header = "Policy Attestation";
    const description = "Your policy has been attested successfully";

    return (
        <>
            <BackSection pageName="Document" to={"/policies"} />
            {appear && (
                <Attestation
                    setAppear={setAppear}
                    targetPath={targetPath}
                    label={label}
                    header={header}
                    description={description}
                />
            )}
            <div className="pt-3 mb-3">
                <div className={`bg-white mb-3 rounded-2 d-flex documents ${css.documentHeader} overflow-hidden d-flex align-items-center justify-content-between`}>
                    <motion.div
                        className={`m-0 py-2 ms-3 ${css.headerText}`}
                        initial={{ x: -100, y: 0 }}
                        animate={{ x: 0, y: 0 }}
                        transition={{
                            type: "spring",
                            damping: 20,
                            duration: 1,
                        }}
                    >
                        <h5 className='m-0'>{Name}</h5>
                    </motion.div>
                    {isAllowed && details && !details.userAcknowledgement && (
                        <div className='d-flex align-items-center'>
                            <div>
                                <label className="custom-checkbox d-flex align-items-center">
                                    <input
                                        className="custom-input"
                                        type="checkbox"
                                        checked={isAcknowledged}
                                        onChange={handleAcknowledgeChange}
                                    />
                                    <span className="custom-checkmark"></span>
                                    <span className="custom-input-label m-0 p-0 fw-bold">
                                        I have read and understood the policy.
                                    </span>
                                </label>
                            </div>
                            <button
                                onClick={handleSave}
                                disabled={!isAcknowledged || acknowledgeMutation.isPending}
                                style={{
                                    padding: "12px 12px",
                                    backgroundColor: isAcknowledged ? "var(--accent)" : "var(--hairline-strong)",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "0px 5px 5px 0px",
                                    cursor: isAcknowledged ? "pointer" : "not-allowed",
                                }}
                                className='ms-2'
                            >
                                Save
                                {acknowledgeMutation.isPending && (
                                    <span className="spinner-border spinner-border-sm ms-2" role="status" aria-hidden="true"></span>
                                )}
                            </button>
                        </div>
                    )}
                </div>

                {isLoading ? (
                    <div className="vh-100">
                        <SkeletonPDF />
                    </div>
                ) : error ? (
                    <div className="vh-100 d-flex align-items-center justify-content-center">
                        <p className="text-danger">Error loading policy details</p>
                    </div>
                ) : details ? (
                    <div className="vh-100">
                        <PDF name={Name} details={details} />
                    </div>
                ) : null}
            </div>
        </>
    );
}